import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SIZES = [["80mm", "Thermal 80mm"], ["100x150", "Label 100 × 150 mm"]];

const download = async (url, params, filename) => {
  const r = await api.get(url, { params, responseType: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(r.data);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export default function Labels() {
  const [assets, setAssets] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [size, setSize] = useState("80mm");
  const [hub, setHub] = useState("");
  const [picked, setPicked] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => setAssets([]));
    api.get("/branches").then((r) => setHubs(r.data)).catch(() => setHubs([]));
  }, []);

  const shown = hub ? assets.filter((a) => a.branch_id === hub) : assets;

  const one = async (a, fmt) => {
    try {
      await download(`/labels/asset/${a.id}`, { size, fmt },
        `label-${a.unit_code}-${size}.${fmt === "png" ? "png" : "pdf"}`);
    } catch (e) { toast.error("Gagal membuat label"); }
  };

  const batch = async () => {
    setBusy(true);
    try {
      const r = await api.post("/labels/batch",
        { kind: "asset", size, ids: picked, branch_id: picked.length ? undefined : hub || undefined },
        { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(r.data);
      link.download = `label-asset-${size}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat PDF gabungan");
    } finally { setBusy(false); }
  };

  const bookingLabel = async (fmt) => {
    if (!code) return;
    try {
      await download(`/labels/booking/${code}`, { size, fmt },
        `label-${code}-${size}.${fmt === "png" ? "png" : "pdf"}`);
    } catch (e) { toast.error("Booking tidak ditemukan"); }
  };

  return (
    <div data-testid="admin-labels" className="pb-24">
      <h1 className="font-display text-3xl tracking-tight">Label &amp; QR</h1>
      <p className="mt-1 text-sm text-ink-soft max-w-2xl">
        QR berisi Asset ID atau kode booking, jadi bisa langsung discan dari halaman Scan di LM Admin.
      </p>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <select data-testid="label-size" value={size} onChange={(e) => setSize(e.target.value)}
          className="h-11 rounded-md border border-input bg-white px-3 text-sm">
          {SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="label-hub" value={hub} onChange={(e) => { setHub(e.target.value); setPicked([]); }}
          className="h-11 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Semua Hub</option>
          {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <button data-testid="batch-labels-btn" disabled={busy} onClick={batch}
          className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
          PDF multi-halaman ({picked.length || shown.length})
        </button>
      </div>

      <div className="mt-6 bg-white border border-black/10 rounded-lg p-5">
        <div className="text-sm font-semibold">Label booking</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="label-booking-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="LM-260101-001" className="h-11 rounded-md border border-input px-3 text-sm" />
          <button data-testid="booking-label-pdf" onClick={() => bookingLabel("pdf")}
            className="px-4 py-2.5 rounded-md border border-black/15 text-sm hover:border-lm-red">PDF</button>
          <button data-testid="booking-label-png" onClick={() => bookingLabel("png")}
            className="px-4 py-2.5 rounded-md border border-black/15 text-sm hover:border-lm-red">PNG QR</button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Pilih</th><th className="p-3">Asset ID</th><th className="p-3">Hub</th>
              <th className="p-3">Warna</th><th className="p-3">Label</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {shown.map((a) => (
              <tr key={a.id} data-testid={`label-row-${a.unit_code}`}>
                <td className="p-3">
                  <input type="checkbox" data-testid={`pick-${a.unit_code}`} checked={picked.includes(a.id)}
                    onChange={() => setPicked((p) => p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id])} />
                </td>
                <td className="p-3 font-medium">{a.unit_code}</td>
                <td className="p-3 text-xs text-ink-soft">{a.hub_name}</td>
                <td className="p-3 text-xs">{a.table_color}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button data-testid={`asset-pdf-${a.unit_code}`} onClick={() => one(a, "pdf")}
                      className="text-xs px-3 py-1.5 rounded border border-black/15 hover:border-lm-red">PDF</button>
                    <button data-testid={`asset-png-${a.unit_code}`} onClick={() => one(a, "png")}
                      className="text-xs px-3 py-1.5 rounded border border-black/15 hover:border-lm-red">PNG</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
